import React, { useEffect, useRef, useState } from "react";
import {
  ScrollView,
  View,
  Text,
  Pressable,
  StyleSheet,
  PanResponder,
  Platform,
  TouchableOpacity,
} from "react-native";
import { Ionicons, Octicons } from "@expo/vector-icons";
import { Provider } from "react-native-paper";
import Toast from "react-native-toast-message";
import { AlertBox } from "../../../components/AlertBox";
import { ADMIN_COLORS, ADMIN_FONT } from "../../../config/theme/adminColors";
import { SectionCard, useResponsive } from "../../AdminPanel/Products/formStyles";
import { SmallText } from "../../../config/theme/globalStyles";

// Espaço vertical entre as linhas. A altura de cada "slot" (linha + gap) é fixa:
// é ela que converte o deslocamento do dedo/mouse (dy) em nº de posições.
const ROW_GAP = 8;

const TYPE_META = {
  live: { icon: "videocam-outline", label: "Aula ao vivo" },
  livestream: { icon: "radio-outline", label: "Livestream" },
  content: { icon: "play-circle-outline", label: "Conteúdo" },
};

// Definido FORA do componente de página: se fosse declarado dentro, o React
// trocaria o tipo do elemento a cada render e remontaria a linha no meio do
// arrasto — o PanResponder seria descartado e o gesto morreria.
const DragRow = ({
  item,
  index,
  total,
  rowH,
  translateY,
  isDragging,
  fullRowDrag,
  onGrant,
  onMove,
  onRelease,
  onMoveUp,
  onMoveDown,
}) => {
  // Os handlers do PanResponder são criados uma única vez, então leem sempre a
  // versão mais recente das callbacks/índice por este ref.
  const latest = useRef({});
  latest.current = { index, onGrant, onMove, onRelease };

  const responder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: (_, gesture) => Math.abs(gesture.dy) > 3,
      onPanResponderGrant: () => latest.current.onGrant(latest.current.index),
      onPanResponderMove: (_, gesture) => latest.current.onMove(gesture.dy),
      onPanResponderRelease: () => latest.current.onRelease(),
      onPanResponderTerminate: () => latest.current.onRelease(),
    })
  ).current;

  const meta = TYPE_META[item.type] || TYPE_META.content;

  return (
    <View
      // No desktop a linha inteira arrasta (o mouse não disputa com o scroll).
      // No mobile só a alça arrasta, senão tocar no título impediria de rolar
      // a lista.
      {...(fullRowDrag ? responder.panHandlers : {})}
      style={[
        styles.row,
        {
          height: rowH - ROW_GAP,
          marginBottom: ROW_GAP,
          transform: [{ translateY }],
          ...(fullRowDrag ? { cursor: isDragging ? "grabbing" : "grab" } : null),
        },
        isDragging && styles.rowDragging,
      ]}
    >
      <View
        {...(fullRowDrag ? {} : responder.panHandlers)}
        style={[styles.handle, !fullRowDrag && { cursor: "grab" }]}
      >
        <Ionicons name="reorder-two-outline" size={22} color={ADMIN_COLORS.placeholder} />
      </View>

      <View style={styles.position}>
        <Text style={styles.positionText}>{index + 1}</Text>
      </View>

      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={styles.rowTitle} numberOfLines={1}>
          {item.title || "Sem título"}
        </Text>
        <View style={styles.rowMeta}>
          <Ionicons name={meta.icon} size={12} color={ADMIN_COLORS.muted} />
          <Text style={styles.rowMetaText}>{meta.label}</Text>
        </View>
      </View>

      {/* Alternativa ao arrasto: ajuste fino no toque, útil no celular. */}
      <View style={styles.stepper}>
        <Pressable
          onPress={onMoveUp}
          disabled={index === 0}
          hitSlop={6}
          style={({ hovered }) => [
            styles.stepBtn,
            hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
            index === 0 && { opacity: 0.3 },
          ]}
        >
          <Ionicons name="chevron-up" size={16} color={ADMIN_COLORS.muted} />
        </Pressable>
        <Pressable
          onPress={onMoveDown}
          disabled={index === total - 1}
          hitSlop={6}
          style={({ hovered }) => [
            styles.stepBtn,
            hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
            index === total - 1 && { opacity: 0.3 },
          ]}
        >
          <Ionicons name="chevron-down" size={16} color={ADMIN_COLORS.muted} />
        </Pressable>
      </View>
    </View>
  );
};

const EditOrder = ({ videos = [], isSaving, onClose, onSave }) => {
  const { isMobile } = useResponsive();
  const rowH = (isMobile ? 64 : 58) + ROW_GAP;
  // Arrastar pela linha toda só no desktop web; no toque a alça evita conflito
  // com o scroll da página.
  const fullRowDrag = Platform.OS === "web" && !isMobile;

  const [updatedVideos, setUpdatedVideos] = useState(videos);
  const [touched, setTouched] = useState(false);
  const [showSaveSuccessAlert, setShowSaveSuccessAlert] = useState(false);

  // Enquanto o admin não mexeu em nada, a lista acompanha o que vem do
  // Firestore. Depois da primeira alteração a ordem local manda, para um
  // snapshot no meio da edição não desfazer o trabalho.
  useEffect(() => {
    if (!touched) setUpdatedVideos(videos);
  }, [videos, touched]);

  // { from, dy, over } enquanto há um arrasto em andamento. O ref espelha o
  // estado porque o "soltar" pode chegar antes do re-render do último move.
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(null);

  const moveItem = (from, to) => {
    setUpdatedVideos((prev) => {
      if (from === to || to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setTouched(true);
  };

  const handleGrant = (from) => {
    dragRef.current = { from, dy: 0, over: from };
    setDrag(dragRef.current);
  };

  const handleMove = (dy) => {
    const current = dragRef.current;
    if (!current) return;
    const steps = Math.round(dy / rowH);
    const over = Math.max(
      0,
      Math.min(updatedVideos.length - 1, current.from + steps)
    );
    dragRef.current = { ...current, dy, over };
    setDrag(dragRef.current);
  };

  const handleRelease = () => {
    const current = dragRef.current;
    dragRef.current = null;
    setDrag(null);
    if (current) moveItem(current.from, current.over);
  };

  // Deslocamento visual das linhas que "abrem espaço" para o item arrastado.
  const shiftFor = (index) => {
    if (!drag) return 0;
    if (index === drag.from) return drag.dy;
    const { from, over } = drag;
    if (from < over && index > from && index <= over) return -rowH;
    if (from > over && index < from && index >= over) return rowH;
    return 0;
  };

  const handleOnSaveClick = async () => {
    try {
      await onSave(updatedVideos);
      setShowSaveSuccessAlert(true);
    } catch (error) {
      // Sem isto o admin via "sucesso" mesmo quando a gravação falhava no meio
      // do loop (ordem só parcialmente atualizada).
      console.error("Falha ao salvar a nova ordem dos conteúdos:", error);
      Toast.show({
        type: "error",
        text1: "Não foi possível salvar a nova ordem dos conteúdos.",
        text2: "Tente novamente mais tarde",
      });
    }
  };

  const closeAlertAndModal = () => {
    setShowSaveSuccessAlert(false);
    onClose();
  };

  return (
    <Provider>
      <ScrollView
        style={{ backgroundColor: ADMIN_COLORS.surfaceSubtle }}
        contentContainerStyle={styles.scrollContent}
        // Durante o arrasto o scroll fica travado: sem isto, no celular, o
        // gesto move a linha e rola a página ao mesmo tempo.
        scrollEnabled={!drag}
      >
        <View style={styles.page}>
          <TouchableOpacity onPress={onClose} style={styles.backLink}>
            <Octicons name="chevron-left" size={18} color={ADMIN_COLORS.muted} />
            <SmallText color={ADMIN_COLORS.muted}>Voltar</SmallText>
          </TouchableOpacity>

          <Text style={styles.pageTitle}>Ordem dos conteúdos</Text>
          <Text style={styles.pageSubtitle}>
            {fullRowDrag
              ? "Arraste os conteúdos para definir a ordem em que aparecem para seus alunos. Você também pode usar as setas."
              : "Arraste pelo ícone ≡ para definir a ordem em que os conteúdos aparecem para seus alunos. Você também pode usar as setas."}
          </Text>

          <SectionCard
            title="Conteúdos deste módulo"
            subtitle={`${updatedVideos.length} ${
              updatedVideos.length === 1 ? "conteúdo" : "conteúdos"
            } — o primeiro da lista aparece no topo`}
          >
            {updatedVideos.length === 0 ? (
              <Text style={styles.empty}>
                Este módulo ainda não tem conteúdos para ordenar.
              </Text>
            ) : (
              <View style={{ height: updatedVideos.length * rowH }}>
                {updatedVideos.map((item, index) => (
                  <DragRow
                    key={item.docId}
                    item={item}
                    index={index}
                    total={updatedVideos.length}
                    rowH={rowH}
                    translateY={shiftFor(index)}
                    isDragging={drag?.from === index}
                    fullRowDrag={fullRowDrag}
                    onGrant={handleGrant}
                    onMove={handleMove}
                    onRelease={handleRelease}
                    onMoveUp={() => moveItem(index, index - 1)}
                    onMoveDown={() => moveItem(index, index + 1)}
                  />
                ))}
              </View>
            )}
          </SectionCard>

          <View style={[styles.actions, isMobile && { flexDirection: "column-reverse" }]}>
            <Pressable
              onPress={onClose}
              disabled={isSaving}
              style={({ hovered }) => [
                styles.btn,
                styles.btnSecondary,
                isMobile && { width: "100%" },
                hovered && { backgroundColor: "#E5E7EB" },
              ]}
            >
              <Text style={[styles.btnText, { color: ADMIN_COLORS.secondaryText }]}>
                Cancelar
              </Text>
            </Pressable>
            <Pressable
              onPress={handleOnSaveClick}
              disabled={isSaving || updatedVideos.length === 0}
              style={({ hovered }) => [
                styles.btn,
                styles.btnPrimary,
                isMobile && { width: "100%" },
                hovered && { backgroundColor: ADMIN_COLORS.accentDark },
                (isSaving || updatedVideos.length === 0) && { opacity: 0.7 },
              ]}
            >
              <Text style={[styles.btnText, { color: ADMIN_COLORS.primaryText }]}>
                {isSaving ? "Salvando..." : "Salvar ordem"}
              </Text>
            </Pressable>
          </View>

          <AlertBox variant="admin"
            message={"Alteração feita com sucesso! :)"}
            visible={showSaveSuccessAlert}
            leftButton={"OK"}
            leftButtonFunction={closeAlertAndModal}
            onClose={closeAlertAndModal}
          />
        </View>
      </ScrollView>
    </Provider>
  );
};

const styles = StyleSheet.create({
  scrollContent: { alignItems: "center", padding: 20 },
  page: { width: "100%", maxWidth: 720 },
  backLink: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    marginBottom: 12,
    alignSelf: "flex-start",
  },
  pageTitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 22,
    fontWeight: "700",
    color: ADMIN_COLORS.ink,
    marginBottom: 4,
  },
  pageSubtitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    color: ADMIN_COLORS.muted,
    marginBottom: 20,
    lineHeight: 20,
  },
  empty: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    color: ADMIN_COLORS.muted,
    paddingVertical: 8,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingHorizontal: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    backgroundColor: ADMIN_COLORS.surface,
  },
  rowDragging: {
    borderColor: ADMIN_COLORS.accent,
    backgroundColor: ADMIN_COLORS.accentSoft,
    zIndex: 10,
    boxShadow: "0 10px 24px rgba(16,24,40,0.18)",
  },
  handle: {
    width: 32,
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "stretch",
  },
  position: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
  },
  positionText: {
    fontFamily: ADMIN_FONT,
    fontSize: 12,
    fontWeight: "700",
    color: ADMIN_COLORS.muted,
  },
  rowTitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    fontWeight: "600",
    color: ADMIN_COLORS.ink,
  },
  rowMeta: { flexDirection: "row", alignItems: "center", gap: 4, marginTop: 2 },
  rowMetaText: { fontFamily: ADMIN_FONT, fontSize: 12, color: ADMIN_COLORS.muted },
  stepper: { flexDirection: "row", gap: 2 },
  stepBtn: {
    width: 28,
    height: 28,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  actions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 12,
    marginTop: 4,
    marginBottom: 24,
  },
  btn: {
    minHeight: 44,
    borderRadius: 10,
    paddingHorizontal: 22,
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  btnPrimary: { backgroundColor: ADMIN_COLORS.accent },
  btnSecondary: { backgroundColor: ADMIN_COLORS.secondaryBg },
  btnText: { fontFamily: ADMIN_FONT, fontSize: 14, fontWeight: "600" },
});

export default EditOrder;
